import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { adminAPI, supportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import socketService from '../../services/socket';
import Navbar from '../../components/Navbar';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: 28.6139, // Delhi default
  lng: 77.2090
};

// Custom marker icons
const getMarkerIcon = (isAvailable, hasActiveOrder) => {
  if (hasActiveOrder) {
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="18" fill="#f97316" stroke="#fff" stroke-width="3"/>
          <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="bold">🛵</text>
        </svg>
      `),
      scaledSize: { width: 40, height: 40 }
    };
  } else if (isAvailable) {
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="18" fill="#22c55e" stroke="#fff" stroke-width="3"/>
          <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="bold">🚴</text>
        </svg>
      `),
      scaledSize: { width: 40, height: 40 }
    };
  }
  return null;
};

// Yellow marker for a RUNNING ORDER's delivery destination (where the food is going).
const getOrderMarkerIcon = () => ({
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
      <circle cx="17" cy="17" r="13" fill="#facc15" stroke="#fff" stroke-width="3"/>
      <text x="17" y="23" text-anchor="middle" font-size="14">📦</text>
    </svg>
  `),
  scaledSize: { width: 34, height: 34 }
});

const LiveDeliveryMap = () => {
  const { user } = useAuth();
  // Support staff share this page but hit /api/support/* (full access).
  const opsAPI = user?.role === 'support' ? supportAPI : adminAPI;
  const [partners, setPartners] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [stats, setStats] = useState({ totalOnline: 0, totalWithOrders: 0, totalPartners: 0 });
  const [noLocationPartners, setNoLocationPartners] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mapRef = useRef(null);

  const fetchPartners = useCallback(async () => {
    try {
      const response = await opsAPI.getDeliveryPartnersForMap();
      if (response.data.success) {
        setPartners(response.data.data.partners);
        setActiveOrders(response.data.data.activeOrderLocations || []);
        setNoLocationPartners(response.data.data.partnersWithoutLocation || []);
        setStats({
          totalOnline: response.data.data.totalOnline,
          totalWithOrders: response.data.data.totalWithOrders,
          totalPartners: response.data.data.totalPartners
        });
        setLastUpdated(new Date());
      }
    } catch (err) {
      setError('Failed to fetch delivery partners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Auto refresh every 10 seconds (fallback for when socket isn't working)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchPartners();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchPartners]);

  // Real-time socket updates — join admin_live_map room and listen
  // for partner_location_update events. Updates the marker position
  // instantly without waiting for the 10-second poll.
  useEffect(() => {
    if (!socketService.isConnected) {
      const token = localStorage.getItem('token');
      socketService.connect(token);
    }

    // Join admin live map room
    socketService.emit('join_admin_live_map');

    // Listen for real-time partner location updates
    const handleLocationUpdate = (data) => {
      if (!data?.deliveryPartnerId || !data?.lat || !data?.lng) return;

      setPartners(prev => prev.map(partner => {
        if (partner._id === data.deliveryPartnerId) {
          return {
            ...partner,
            location: {
              ...partner.location,
              lat: data.lat,
              lng: data.lng,
              lastUpdated: data.timestamp || new Date().toISOString()
            }
          };
        }
        return partner;
      }));
      setLastUpdated(new Date());
    };

    const unsubscribe = socketService.on('partner_location_update', handleLocationUpdate);

    return () => {
      unsubscribe();
      socketService.emit('leave_admin_live_map');
    };
  }, []);

  const handleMarkerClick = async (partner) => {
    setSelectedOrder(null);
    setSelectedPartner(partner);
    setOrderDetails(null);

    if (partner.hasActiveOrder) {
      setOrderLoading(true);
      try {
        const response = await opsAPI.getDeliveryPartnerCurrentOrder(partner._id);
        if (response.data.success) {
          setOrderDetails(response.data.data.currentOrder);
        }
      } catch (err) {
        console.error('Failed to fetch order details:', err);
      } finally {
        setOrderLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      restaurant_confirmed: { bg: '#e0e7ff', color: '#4338ca', text: 'Confirmed' },
      preparing: { bg: '#fef3c7', color: '#b45309', text: 'Preparing' },
      ready_for_pickup: { bg: '#fae8ff', color: '#a21caf', text: 'Ready' },
      assigned_delivery: { bg: '#dbeafe', color: '#1d4ed8', text: 'Rider Assigned' },
      accepted: { bg: '#dbeafe', color: '#1d4ed8', text: 'Accepted' },
      picked_up: { bg: '#fef3c7', color: '#d97706', text: 'Picked Up' },
      on_the_way: { bg: '#d1fae5', color: '#059669', text: 'On The Way' }
    };
    const style = statusColors[status] || { bg: '#e5e7eb', color: '#374151', text: status };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {style.text}
      </span>
    );
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            🗺️ Live Delivery Map
          </h2>
          <p style={{ color: '#6b7280' }}>
            Real-time view of all delivery partners and their current orders
          </p>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>🟢</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>{stats.totalOnline}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Online Partners</div>
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>🛵</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f97316' }}>{stats.totalWithOrders}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>On Delivery</div>
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>📍</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>{stats.totalPartners}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Total on Map</div>
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>📦</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#eab308' }}>{activeOrders.length}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Running Orders</div>
            </div>
          </div>

          <div style={{
            background: noLocationPartners.length > 0 ? '#fffbeb' : 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: noLocationPartners.length > 0 ? '1px solid #f59e0b' : '1px solid transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>📡</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>{noLocationPartners.length}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Online — No GPS</div>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Auto-refresh (30s)</span>
            </label>
            <button
              onClick={fetchPartners}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: '#3b82f6',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              🔄 {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {lastUpdated && (
          <div style={{ marginBottom: '16px', fontSize: '12px', color: '#9ca3af' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        )}

        {/* Legend */}
        <div style={{
          background: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          gap: '24px',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Legend:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#22c55e' }}></div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Available (Online)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#f97316' }}></div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>On Delivery (Has Order)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#facc15' }}></div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Running Order (Delivery Location)</span>
          </div>
        </div>

        {/* Map */}
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          {error ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              {error}
            </div>
          ) : (
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={12}
                onLoad={(map) => { mapRef.current = map; }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true
                }}
              >
                {partners.map((partner) => (
                  <Marker
                    key={partner._id}
                    position={{ lat: partner.location.lat, lng: partner.location.lng }}
                    icon={getMarkerIcon(partner.isAvailable, partner.hasActiveOrder)}
                    onClick={() => handleMarkerClick(partner)}
                  />
                ))}

                {/* Running orders — delivery destinations (yellow) */}
                {activeOrders.map((order, i) => (
                  <Marker
                    key={`order-${order.orderId}-${i}`}
                    position={{ lat: order.lat, lng: order.lng }}
                    icon={getOrderMarkerIcon()}
                    onClick={() => { setSelectedOrder(order); setSelectedPartner(null); }}
                  />
                ))}

                {selectedOrder && (
                  <InfoWindow
                    position={{ lat: selectedOrder.lat, lng: selectedOrder.lng }}
                    onCloseClick={() => setSelectedOrder(null)}
                  >
                    <div style={{ padding: '8px', minWidth: '240px', maxWidth: '320px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>📦</span>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>#{selectedOrder.orderId}</h3>
                        {getStatusBadge(selectedOrder.status)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600' }}>🏪 Restaurant:</span> {selectedOrder.restaurantName}
                        </div>
                        <div>
                          <span style={{ fontWeight: '600' }}>📍 Deliver to:</span> {selectedOrder.customerName || 'Customer'}
                        </div>
                        {selectedOrder.deliveryStreet && (
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{selectedOrder.deliveryStreet}</div>
                        )}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px'
                        }}>
                          <span>{selectedOrder.hasPartner ? '🛵 Rider assigned' : '⏳ Awaiting rider'}</span>
                          <span style={{ fontWeight: '600' }}>₹{selectedOrder.total}</span>
                        </div>
                        {selectedOrder.orderType && selectedOrder.orderType !== 'food' && (
                          <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '4px' }}>
                            {selectedOrder.orderType.replace(/_/g, ' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </InfoWindow>
                )}

                {selectedPartner && (
                  <InfoWindow
                    position={{
                      lat: selectedPartner.location.lat,
                      lng: selectedPartner.location.lng
                    }}
                    onCloseClick={() => {
                      setSelectedPartner(null);
                      setOrderDetails(null);
                    }}
                  >
                    <div style={{ padding: '8px', minWidth: '280px', maxWidth: '350px' }}>
                      {/* Partner Info */}
                      <div style={{
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '20px' }}>
                            {selectedPartner.vehicleType === 'bike' ? '🚴' : '🛵'}
                          </span>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {selectedPartner.name}
                          </h3>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: selectedPartner.hasActiveOrder ? '#fef3c7' : '#d1fae5',
                            color: selectedPartner.hasActiveOrder ? '#d97706' : '#059669'
                          }}>
                            {selectedPartner.hasActiveOrder ? 'On Delivery' : 'Available'}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          <div>📞 {selectedPartner.phone}</div>
                          <div>⭐ {selectedPartner.rating.toFixed(1)} • {selectedPartner.totalDeliveries} deliveries</div>
                          <div>📍 {selectedPartner.workArea}</div>
                        </div>
                      </div>

                      {/* Order Info */}
                      {selectedPartner.hasActiveOrder && (
                        <div>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                            📦 Current Order
                          </h4>
                          {orderLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                              Loading order details...
                            </div>
                          ) : orderDetails ? (
                            <div style={{ fontSize: '13px' }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <span style={{ fontWeight: '600' }}>#{orderDetails.orderId}</span>
                                {getStatusBadge(orderDetails.status)}
                              </div>
                              <div style={{
                                background: '#f9fafb',
                                padding: '10px',
                                borderRadius: '8px',
                                marginBottom: '8px'
                              }}>
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontWeight: '600' }}>🏪 Restaurant:</span>
                                  <div style={{ color: '#374151' }}>{orderDetails.restaurant?.name}</div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {orderDetails.restaurant?.address?.street}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ fontWeight: '600' }}>📍 Delivery To:</span>
                                  <div style={{ color: '#374151' }}>{orderDetails.customer?.name}</div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {orderDetails.deliveryAddress?.street}
                                  </div>
                                </div>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                borderTop: '1px solid #e5e7eb',
                                paddingTop: '8px'
                              }}>
                                <span>💰 Total: ₹{orderDetails.totalAmount}</span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  background: orderDetails.paymentMethod === 'cash' ? '#fef3c7' : '#dbeafe',
                                  color: orderDetails.paymentMethod === 'cash' ? '#d97706' : '#1d4ed8'
                                }}>
                                  {orderDetails.paymentMethod === 'cash' ? '💵 COD' : '💳 Online'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                              Order info: {selectedPartner.currentOrder?.orderId}
                            </div>
                          )}
                        </div>
                      )}

                      {!selectedPartner.hasActiveOrder && (
                        <div style={{
                          textAlign: 'center',
                          padding: '16px',
                          background: '#f0fdf4',
                          borderRadius: '8px',
                          color: '#15803d'
                        }}>
                          ✅ Available for new orders
                        </div>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </LoadScript>
          )}
        </div>

        {/* Partners List */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '16px',
          marginTop: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            📋 Delivery Partners List ({partners.length})
          </h3>
          {partners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              No delivery partners currently online with location data
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Partner</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Phone</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Current Order</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Rating</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr
                      key={partner._id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onClick={() => {
                        handleMarkerClick(partner);
                        mapRef.current?.panTo({ lat: partner.location.lat, lng: partner.location.lng });
                        mapRef.current?.setZoom(15);
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{partner.vehicleType === 'bike' ? '🚴' : '🛵'}</span>
                          <span style={{ fontWeight: '500' }}>{partner.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>{partner.phone}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: partner.hasActiveOrder ? '#fef3c7' : '#d1fae5',
                          color: partner.hasActiveOrder ? '#d97706' : '#059669'
                        }}>
                          {partner.hasActiveOrder ? '🛵 On Delivery' : '🟢 Available'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {partner.currentOrder ? (
                          <div>
                            <div style={{ fontWeight: '500' }}>#{partner.currentOrder.orderId}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {partner.currentOrder.restaurantName}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        ⭐ {partner.rating.toFixed(1)} ({partner.totalDeliveries})
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                        {partner.location.lat.toFixed(4)}, {partner.location.lng.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Online but no GPS location — surfaced so online partners don't silently vanish */}
        {noLocationPartners.length > 0 && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            padding: '20px',
            borderRadius: '16px',
            marginTop: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#b45309' }}>
              📡 Online — Location Unavailable ({noLocationPartners.length})
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#92400e' }}>
              These partners are online but their app hasn't shared a GPS location, so they can't be pinned on the map.
              Ask them to turn on Location/GPS and grant the delivery app location permission ("Allow all the time"),
              then toggle offline → online again.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fef3c7' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Partner</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Phone</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {noLocationPartners.map((p) => (
                    <tr key={p._id} style={{ borderBottom: '1px solid #fde68a' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{p.vehicleType === 'bike' ? '🚴' : '🛵'}</span>
                          <span style={{ fontWeight: '500' }}>{p.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>{p.phone}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: p.hasActiveOrder ? '#fef3c7' : '#d1fae5',
                          color: p.hasActiveOrder ? '#d97706' : '#059669'
                        }}>
                          {p.hasActiveOrder ? '🛵 On Delivery' : '🟢 Online'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveDeliveryMap;
